import React, { useState } from 'react';
import { updatePassword } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

import Card from '../ui/Card/Card';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import { ICONS } from '../ui/icon-map';
import { Field, Input } from '../ui/Field/Field';
import { toast } from '../ui/feedback';

const ChangePasswordPage = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // ProtectedRoute sends users here while this flag is set; otherwise they came voluntarily.
    const forced = Boolean(currentUser?.security?.forceChangePassword);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            return setError("Passwords do not match");
        }

        if (password.length < 6) {
            return setError("Password must be at least 6 characters");
        }

        setLoading(true);
        try {
            if (auth.currentUser) {
                // 1. Update Password in Auth
                await updatePassword(auth.currentUser, password);

                // 2. Update Firestore Security Metadata
                if (currentUser && currentUser.uid) {
                    await updateDoc(doc(db, "users", currentUser.uid), {
                        "security.forceChangePassword": false, "security.passwordLastChanged": new Date().toISOString()
                    });
                }

                toast.success("Password updated successfully!");
                navigate('/');
            } else {
                setError("No active session found.");
            }
        } catch (err) {
            logger.error("Change Password Error:", err);
            setError("Failed to change password. You may need to logout and login again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout title="Change Password">
            <div className="flex justify-center pt-6">
                <Card
                    className="max-w-md w-full"
                    title={forced ? 'Security Update Required' : 'Change password'}
                    subtitle={forced
                        ? 'For your security, please update your password to continue accessing the system.'
                        : 'Choose a new password of at least 6 characters.'}
                >
                    {error && (
                        <div
                            role="alert"
                            className="flex items-start gap-2 px-3 py-2 mb-4 rounded-md border text-sm"
                            style={{ background: 'var(--danger-soft)', borderColor: 'var(--danger-200)', color: 'var(--text)' }}
                        >
                            <Icon glyph={ICONS.error} size="sm" tone="danger" className="mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                        <Field label="New Password" required>
                            <Input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                minLength={6}
                                autoComplete="new-password"
                                placeholder="Enter new password"
                            />
                        </Field>
                        <Field label="Confirm Password" required>
                            <Input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                minLength={6}
                                autoComplete="new-password"
                                placeholder="Confirm new password"
                            />
                        </Field>

                        <Button type="submit" variant="primary" fullWidth isLoading={loading}>
                            {loading ? 'Updating...' : 'Update Password'}
                        </Button>
                    </form>
                </Card>
            </div>
        </Layout>
    );
};

export default ChangePasswordPage;
